/* SellerConfidencePanel.jsx — Steward-only Deal Room reassurance preview.
 *
 * Renders inside the right intelligence rail of `<DealRoomPage />`. Shows
 * the exact calm payload sellers receive in Friday's comfort beat — this
 * week's progress, current state, the calm blocker line, and the
 * reassurance closer. Provides a "Send comfort beat now" manual override
 * with a calm confirmation step and resend protection.
 *
 * Doctrine:
 *   - Steward-only (admin / director / manager) — hidden otherwise.
 *   - Auto-hides if the preview fails to load (calm degrade).
 *   - No noisy notifications. No marketing language. No dashboard bloat.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ShieldCheck, Mail, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../data/authStore";

const STEWARD_ROLES = new Set(["admin", "director", "manager"]);

const cardStyle = {
  borderRadius: "16px",
  border: "1px solid var(--gp-card-border, #E2E8F0)",
  background: "var(--gp-card-bg, #FFFFFF)",
  boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
};

const accentStyle = {
  background:
    "linear-gradient(135deg, rgba(139,92,246,0.06), rgba(99,102,241,0.03))",
  border: "1px solid rgba(139,92,246,0.18)",
};

export function SellerConfidencePanel({ dealId }) {
  const { user } = useAuth();
  const isSteward = useMemo(
    () => STEWARD_ROLES.has(((user?.role || "") + "").toLowerCase()),
    [user],
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentInfo, setSentInfo] = useState(null);

  const refetch = useCallback(async () => {
    if (!dealId || !isSteward) {
      setLoading(false);
      return;
    }
    try {
      const res = await api.get(
        `/api/seller-confidence/deal/${dealId}/preview`,
      );
      setData(res.data || null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dealId, isSteward]);

  useEffect(() => {
    setLoading(true);
    setData(null);
    setSentInfo(null);
    refetch();
  }, [refetch]);

  const handleSendClick = () => {
    if (sending) return;
    setConfirmOpen(true);
  };

  const handleConfirmSend = async () => {
    if (sending) return;
    setSending(true);
    try {
      const res = await api.post(
        `/api/seller-confidence/deal/${dealId}/send`,
      );
      const body = res.data || {};
      if (body.ok) {
        setSentInfo({
          to: body.to,
          iso_week: body.iso_week,
          subject: body.subject,
        });
        toast.success("Comfort beat sent");
      } else {
        toast.error("Comfort beat could not be sent");
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Send failed";
      if (status === 503) {
        toast.error("Email is not configured on this environment");
      } else if (status === 400) {
        toast.error(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setSending(false);
      setConfirmOpen(false);
    }
  };

  if (!isSteward) return null;
  if (loading) return null;
  if (!data || !data.preview) return null;

  const preview = data.preview;
  const seller = preview.seller || {};
  const property = preview.property || {};
  const progression = preview.progression || {};
  const thisWeek = Array.isArray(preview.this_week) ? preview.this_week : [];
  const sellerHasEmail = !!(seller.email && seller.email.trim());
  const emailReady = data.email_configured !== false;
  const buttonDisabled = sending || !sellerHasEmail || !emailReady;

  return (
    <section
      className="p-4"
      style={cardStyle}
      data-testid="seller-confidence-panel"
    >
      <div className="flex items-center gap-1.5 mb-1">
        <ShieldCheck
          size={11}
          strokeWidth={2.3}
          className="text-violet-600"
        />
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-violet-700">
          Seller reassurance · Friday beat
        </p>
      </div>
      <h3
        className="text-[13px] font-semibold text-slate-900 leading-tight"
        data-testid="seller-confidence-headline"
      >
        {preview.headline}
      </h3>
      <p
        className="text-[11.5px] text-slate-500 leading-snug mt-0.5"
        data-testid="seller-confidence-address"
      >
        {property.address}
      </p>

      {/* Current state · the calm violet tile */}
      <div
        className="mt-3 px-3 py-2.5 rounded-xl"
        style={accentStyle}
        data-testid="seller-confidence-progression"
      >
        <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold text-violet-700">
          Current state
        </p>
        <p className="text-[12.5px] font-semibold text-indigo-950 mt-0.5">
          {progression.label}
        </p>
      </div>

      {/* This week */}
      {thisWeek.length > 0 ? (
        <div className="mt-3" data-testid="seller-confidence-this-week">
          <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold text-slate-400 mb-1.5">
            What progressed this week
          </p>
          <ul className="space-y-1.5">
            {thisWeek.map((item, i) => (
              <li
                key={`${item.phrase}-${i}`}
                className="flex items-start gap-2 text-[12px] text-slate-700 leading-snug"
                data-testid="seller-confidence-progress-item"
              >
                <span
                  className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ background: "#10B981" }}
                />
                <span>{item.phrase}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p
          className="mt-3 text-[11.5px] text-slate-500 leading-snug"
          data-testid="seller-confidence-quiet-week"
        >
          Operational coordination has continued behind the scenes this week.
          Your transaction remains on track.
        </p>
      )}

      {/* Blocker line — calm amber */}
      {preview.blocker_line ? (
        <div
          className="mt-3 px-3 py-2 rounded-xl"
          style={{
            background: "#FFFBEB",
            border: "1px solid #FEF3C7",
          }}
          data-testid="seller-confidence-blocker"
        >
          <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold text-amber-800">
            Currently being coordinated
          </p>
          <p className="text-[12px] text-amber-900 leading-snug mt-0.5">
            {preview.blocker_line}
          </p>
        </div>
      ) : null}

      {/* Next step */}
      <div className="mt-3" data-testid="seller-confidence-next-step">
        <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold text-slate-400 mb-1">
          What happens next
        </p>
        <p className="text-[12px] text-slate-700 leading-snug">
          {preview.next_step}
        </p>
      </div>

      {/* Reassurance — calm green close */}
      <div
        className="mt-3 px-3 py-2 rounded-xl flex items-start gap-2"
        style={{ background: "#F0FDF4", border: "1px solid #DCFCE7" }}
        data-testid="seller-confidence-reassurance"
      >
        <span
          className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0"
          style={{ background: "#10B981" }}
        />
        <p className="text-[12px] text-emerald-800 leading-snug">
          {preview.reassurance}
        </p>
      </div>

      {/* Recipient + override */}
      <div className="mt-3 pt-3 border-t border-slate-100">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold text-slate-400">
              Sent to
            </p>
            <p
              className="text-[12px] font-medium text-slate-700 truncate"
              data-testid="seller-confidence-recipient"
            >
              {seller.name || "Seller"}
              {seller.email ? (
                <span className="text-slate-400">
                  {" · "}
                  {seller.email}
                </span>
              ) : (
                <span className="text-amber-600">
                  {" · "}no email on file
                </span>
              )}
            </p>
          </div>
        </div>

        {sentInfo ? (
          <div
            className="mt-3 px-3 py-2 rounded-xl flex items-center gap-2"
            style={{ background: "#F0FDF4", border: "1px solid #DCFCE7" }}
            data-testid="seller-confidence-sent-confirmation"
          >
            <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
            <p className="text-[11.5px] text-emerald-800 leading-snug">
              Comfort beat sent · {sentInfo.iso_week} · {sentInfo.to}
            </p>
          </div>
        ) : confirmOpen ? (
          <div
            className="mt-3 px-3 py-3 rounded-xl"
            style={{ background: "#FAFBFD", border: "1px solid #E2E8F0" }}
            data-testid="seller-confidence-confirm"
          >
            <p className="text-[12px] text-slate-700 leading-snug">
              Send this calm weekly update to {seller.name || "the seller"} now?
            </p>
            <p className="text-[11px] text-slate-500 leading-snug mt-1">
              This won't duplicate the Friday delivery — one beat per
              seller per ISO week.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={handleConfirmSend}
                disabled={sending}
                className="h-9 px-3 rounded-lg text-[12px] font-semibold text-white inline-flex items-center gap-1.5 disabled:opacity-60"
                style={{
                  background:
                    "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
                  boxShadow:
                    "0 8px 18px -10px rgba(99,102,241,0.55)",
                }}
                data-testid="seller-confidence-confirm-send"
              >
                {sending ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Mail size={13} />
                )}
                {sending ? "Sending…" : "Yes, send now"}
              </button>
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={sending}
                className="h-9 px-3 rounded-lg text-[12px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60"
                data-testid="seller-confidence-confirm-cancel"
              >
                Not yet
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleSendClick}
            disabled={buttonDisabled}
            className="mt-3 w-full h-10 rounded-xl text-[12.5px] font-semibold inline-flex items-center justify-center gap-1.5 transition disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              border: "1px solid rgba(139,92,246,0.28)",
              background:
                "linear-gradient(135deg, rgba(139,92,246,0.06), rgba(99,102,241,0.03))",
              color: "#5B21B6",
            }}
            data-testid="seller-confidence-send-button"
          >
            <Mail size={13} />
            Send comfort beat now
          </button>
        )}

        {!sellerHasEmail ? (
          <p
            className="text-[10.5px] text-amber-700 mt-2 leading-snug"
            data-testid="seller-confidence-no-email"
          >
            Add the seller's email to the participants list to enable
            this weekly reassurance.
          </p>
        ) : !emailReady ? (
          <p
            className="text-[10.5px] text-slate-500 mt-2 leading-snug"
            data-testid="seller-confidence-email-not-configured"
          >
            Email delivery isn't configured on this environment yet.
          </p>
        ) : null}
      </div>
    </section>
  );
}

export default SellerConfidencePanel;
